import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AdminProvider, AdminRule, RuleRunResult } from '@shared/types.ts';
import { api, type RuleInput } from '@/lib/api.ts';
import { Badge, ConfirmButton, Field, Problem, Switch, when } from './bits.tsx';

/**
 * "KEEP THIS CATEGORY IN THIS BLOCK OF NUMBERS."
 *
 * The one thing on these screens that is allowed to DELETE, and the screen is
 * written to make that visible rather than to hide it. A rule may delete
 * because it can prove what is its: everything it created carries its id.
 * An import cannot prove that, so an import never deletes.
 *
 * WHY IT EARNS ITS KEEP: a sports category turns over completely. Today's MLB
 * channels are not tomorrow's, and without a rule the lineup is either
 * re-imported by hand every morning or fills quietly with dead entries.
 *
 * PREVIEW IS OFFERED ON EVERY ROW, because "what would this do" is the
 * question somebody has the moment they can see it deletes, and answering it
 * by running it is not an answer.
 */
export function Rules() {
  const [adding, setAdding] = useState(false);

  const rules = useQuery({
    queryKey: ['admin', 'rules'],
    queryFn: () => api.adminRules(),
    staleTime: 10_000,
  });
  const providers = useQuery({
    queryKey: ['admin', 'providers'],
    queryFn: () => api.adminProviders(),
    staleTime: 60_000,
  });
  const server = useQuery({
    queryKey: ['admin', 'server'],
    queryFn: () => api.adminServer(),
    staleTime: 60_000,
  });

  if (rules.isLoading) return <div className="muted center">Loading rules…</div>;
  if (rules.error) return <Problem error={rules.error} />;

  const at = server.data
    ? `${String(server.data.autoSync.hour).padStart(2, '0')}:`
      + `${String(server.data.autoSync.minute).padStart(2, '0')}`
    : null;

  return (
    <>
      <p className="muted admin-note">
        A rule reconciles one category against one block of channel numbers, once a
        day{at && <> at <strong>{at}</strong> server time</>}, and on demand from here.
        It only ever touches channels it created itself — a channel you added by hand
        that happens to sit inside the range is not its to remove. A run that would
        delete more than half of what it owns while adding nothing stops instead, on
        the grounds that a provider having a bad morning looks exactly like a category
        that genuinely emptied.
      </p>

      <div className="rule-list">
        {(rules.data ?? []).map((r) => (
          <RuleCard key={r.id} rule={r} providers={providers.data ?? []} />
        ))}
        {rules.data?.length === 0 && <p className="muted">No rules yet.</p>}
      </div>

      {adding
        ? <RuleForm providers={providers.data ?? []} onDone={() => setAdding(false)} />
        : (
          <button className="btn primary admin-add" onClick={() => setAdding(true)}>
            Add a rule
          </button>
        )}
    </>
  );
}

function RuleCard({ rule: r, providers }: { rule: AdminRule; providers: AdminProvider[] }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [preview, setPreview] = useState<RuleRunResult | null>(null);
  const [ran, setRan] = useState<RuleRunResult | null>(null);

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['admin', 'rules'] });
    void qc.invalidateQueries({ queryKey: ['admin', 'channels'] });
  };

  const toggle = useMutation({
    mutationFn: (enabled: boolean) => api.adminRulePatch(r.id, { enabled }),
    onSuccess: refresh,
  });

  const ask = useMutation({
    mutationFn: () => api.adminRuleRun(r.id, true),
    onSuccess: (x) => { setPreview(x.result); setRan(null); },
  });

  const run = useMutation({
    mutationFn: () => api.adminRuleRun(r.id, false),
    onSuccess: (x) => { setRan(x.result); setPreview(null); refresh(); },
  });

  const remove = useMutation({
    mutationFn: () => api.adminRuleDelete(r.id),
    onSuccess: refresh,
  });

  const provider = providers.find((p) => p.id === r.providerId);

  return (
    <section className={`rule${r.enabled ? '' : ' is-off'}`}>
      <header className="provider-head">
        <div className="provider-who">
          <div className="admin-name">{r.label}</div>
          <div className="muted admin-sub">
            <Badge>{provider?.name ?? 'provider missing'}</Badge>{' '}
            <Badge>{r.categoryName || 'everything on the account'}</Badge>{' '}
            <Badge>{r.numberRangeStart}–{r.numberRangeEnd}</Badge>{' '}
            <Badge tone={r.channels ? undefined : 'off'}>
              owns {r.channels}
            </Badge>
          </div>
          <div className="muted admin-sub">
            Last run {when(r.lastRunAt)}
            {r.lastRunResult && (
              r.lastRunResult.refused
                ? <span className="admin-unlinked"> · refused: {r.lastRunResult.refused}</span>
                : <> · +{r.lastRunResult.added} −{r.lastRunResult.removed}
                    {' '}kept {r.lastRunResult.kept}</>
            )}
          </div>
        </div>

        <div className="provider-actions">
          <Switch
            on={r.enabled}
            disabled={toggle.isPending}
            label={`${r.label} runs automatically`}
            onChange={(v) => toggle.mutate(v)}
          />
          <button className="btn" disabled={ask.isPending} onClick={() => ask.mutate()}>
            {ask.isPending ? 'Checking…' : 'Preview'}
          </button>
          <button className="btn primary" disabled={run.isPending} onClick={() => run.mutate()}>
            {run.isPending ? 'Running…' : 'Run now'}
          </button>
          <button className="btn" onClick={() => setEditing(!editing)}>
            {editing ? 'Close' : 'Edit'}
          </button>
          {/* Deleting a rule KEEPS its channels and clears the stamp, so this
              is not the destructive button it looks like. Says so below. */}
          <ConfirmButton
            disabled={remove.isPending}
            onConfirm={() => remove.mutate()}
            armedLabel={r.channels ? `Delete — keeps its ${r.channels} channels` : 'Really delete?'}
          >
            Delete
          </ConfirmButton>
        </div>
      </header>

      {preview && <RunSummary result={preview} verb="would" />}
      {ran && <RunSummary result={ran} verb="did" />}

      <Problem error={toggle.error ?? ask.error ?? run.error ?? remove.error} />

      {editing && (
        <RuleForm
          rule={r}
          providers={providers}
          onDone={() => { setEditing(false); refresh(); }}
          onCancel={() => setEditing(false)}
        />
      )}
    </section>
  );
}

function RunSummary({ result, verb }: { result: RuleRunResult; verb: 'would' | 'did' }) {
  if (result.refused) {
    return <p className="admin-error">Refused: {result.refused}</p>;
  }
  return (
    <div className={`import-result${verb === 'did' ? ' is-done' : ''}`}>
      {verb === 'would' ? 'Would add' : 'Added'} <strong>{result.added}</strong>,
      {' '}{verb === 'would' ? 'remove' : 'removed'} <strong>{result.removed}</strong>,
      {' '}keep {result.kept}, refresh {result.updated}.
    </div>
  );
}

/**
 * Making or changing a rule.
 *
 * THE CATEGORY IS PICKED FROM WHAT THE PROVIDER ACTUALLY OFFERS, never typed.
 * An Xtream category is an opaque numeric id and a playlist group is an exact
 * string — both are things a person gets subtly wrong by hand, and the
 * failure is a rule that quietly matches nothing and reports "removed
 * everything" on its first run.
 */
function RuleForm({
  rule, providers, onDone, onCancel,
}: {
  rule?: AdminRule;
  providers: AdminProvider[];
  onDone: () => void;
  onCancel?: () => void;
}) {
  const qc = useQueryClient();
  const usable = providers.filter(
    (p) => p.configured && (p.kind === 'xtream' || p.kind === 'm3u' || p.kind === 'iheart'),
  );

  const [providerId, setProviderId] = useState(rule?.providerId ?? usable[0]?.id ?? '');
  const [categoryId, setCategoryId] = useState(rule?.categoryId ?? '');
  const [label, setLabel] = useState(rule?.label ?? '');
  const [start, setStart] = useState(rule ? String(rule.numberRangeStart) : '');
  const [end, setEnd] = useState(rule ? String(rule.numberRangeEnd) : '');

  const groups = useQuery({
    queryKey: ['admin', 'providers', providerId, 'groups'],
    queryFn: () => api.adminProviderGroups(providerId),
    enabled: !!providerId,
    staleTime: 5 * 60_000,
    retry: false,
  });

  const save = useMutation({
    mutationFn: () => {
      const name = groups.data?.find((g) => g.id === categoryId)?.name ?? '';
      const body: RuleInput = {
        label: label.trim() || name || undefined,
        providerId,
        categoryId,
        categoryName: name,
        numberRangeStart: Number(start),
        numberRangeEnd: Number(end),
      };
      if (rule) return api.adminRulePatch(rule.id, body);
      return api.adminRuleCreate(body);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin', 'rules'] });
      onDone();
    },
  });

  const rangeBad = start === '' || end === '' || Number(end) < Number(start);
  const blocked = !providerId || rangeBad || save.isPending;

  return (
    <form
      className="provider-form rule-form"
      onSubmit={(e) => { e.preventDefault(); if (!blocked) save.mutate(); }}
    >
      <Field label="Provider">
        <select
          className="seerr-input"
          value={providerId}
          onChange={(e) => { setProviderId(e.target.value); setCategoryId(''); }}
        >
          {usable.length === 0 && <option value="">No configured provider can be synced</option>}
          {usable.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </Field>

      <Field
        label="Category"
        hint={groups.isLoading
          ? 'Asking the provider…'
          : 'Picked from what the provider offers, never typed.'}
      >
        <select
          className="seerr-input"
          value={categoryId}
          disabled={groups.isLoading}
          onChange={(e) => setCategoryId(e.target.value)}
        >
          {/*
            "Everything" is offered EXPLICITLY rather than being what an empty
            box happens to mean. A rule over a whole account is a legitimate
            thing to want and a terrible thing to get by accident.
          */}
          <option value="">Everything on this account</option>
          {(groups.data ?? []).map((g) => (
            <option key={g.id} value={g.id}>
              {g.name}{g.count != null ? ` (${g.count})` : ''}
            </option>
          ))}
        </select>
      </Field>

      <Field label="Name" hint="Optional — the category's own name is used otherwise.">
        <input
          className="seerr-input"
          value={label}
          placeholder={groups.data?.find((g) => g.id === categoryId)?.name ?? 'MLB'}
          onChange={(e) => setLabel(e.target.value)}
        />
      </Field>

      <Field label="Numbers from" hint="Two rules may not share numbers.">
        <input
          className="seerr-input"
          value={start}
          inputMode="numeric"
          placeholder="600"
          onChange={(e) => setStart(e.target.value.replace(/\D/g, ''))}
        />
      </Field>
      <Field label="to">
        <input
          className="seerr-input"
          value={end}
          inputMode="numeric"
          placeholder="650"
          onChange={(e) => setEnd(e.target.value.replace(/\D/g, ''))}
        />
      </Field>

      <div className="provider-form-actions">
        <button className="btn primary" type="submit" disabled={blocked}>
          {save.isPending ? 'Saving…' : rule ? 'Save' : 'Add rule'}
        </button>
        {onCancel && <button className="btn" type="button" onClick={onCancel}>Cancel</button>}
        {!onCancel && <button className="btn" type="button" onClick={onDone}>Cancel</button>}
      </div>

      <div className="provider-form-problem">
        {rangeBad && start !== '' && end !== '' && (
          <p className="admin-error">That range runs backwards.</p>
        )}
        <Problem error={save.error} />
        <Problem error={groups.error} />
      </div>
    </form>
  );
}
